import React, { useEffect, useRef } from "react";
import { X, Mail, Search } from "lucide-react";
import { DEFAULT_CONTACT_EMAIL } from "../../utils/constants";
import { ACCELERATOR_CATEGORY_MAP } from "../../utils/accelerators/constants";

import { StatusBadge } from "../../shared/ui";

export function getCategoryConfig(category) {
  const normalized = String(category || "").toLowerCase().replace(/[\s_-]+/g, "");
  return ACCELERATOR_CATEGORY_MAP[normalized] || ACCELERATOR_CATEGORY_MAP.default;
}

/* ---------------- Details Modal ---------------- */

export function DetailsModal({ accelerator, onClose, onCopyEmail }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'button, a[href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!accelerator) return null;
  const a = accelerator;

  return (
    <div
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 z-[70] animate-[acc-overlay-in_0.16s_ease]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative flex w-full max-w-2xl max-h-[92vh] sm:max-h-[80vh] bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl animate-[acc-modal-in_0.2s_ease]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`modal-title-${a.id}`}
        ref={dialogRef}
      >
        <div className={`w-[5px] shrink-0 ${getCategoryConfig(a.category).railClass}`} aria-hidden="true" />

        <div className="flex-1 flex flex-col min-w-0 max-h-[92vh] sm:max-h-[80vh]">
          <div className="flex items-start justify-between gap-3 p-5 border-b border-line shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div id={`modal-title-${a.id}`} className="text-lg font-extrabold tracking-tight text-ink">
                  {a.title}
                </div>
                <StatusBadge map={ACCELERATOR_CATEGORY_MAP} status={a.category} size="sm" />
              </div>
              <div className="mt-1 text-muted text-[13px] leading-relaxed">{a.tagline}</div>
            </div>
            <button
              className="border border-line bg-white text-ink w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer shrink-0 hover:bg-gray-50 transition-colors"
              type="button"
              onClick={onClose}
              ref={closeBtnRef}
              aria-label="Close details"
            >
              <X size={16} strokeWidth={1.8} style={{ display: "block", flexShrink: 0 }} />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            <div className="p-5 border-b border-line">
              <div className="text-[11.5px] font-extrabold tracking-widest uppercase text-muted mb-2.5">Summary</div>
              <div className="space-y-2.5 text-[13px] leading-relaxed text-ink">
                {(a.overview || []).map((p, idx) => (
                  <p key={idx} className="m-0">{p}</p>
                ))}
              </div>
            </div>

            <div className="p-5">
              <div className="text-[11.5px] font-extrabold tracking-widest uppercase text-muted mb-2.5">Benefits</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(a.benefits || []).map((b) => (
                  <div className="border border-line rounded-xl p-3 bg-gray-50" key={b.title}>
                    <div className="text-[12.5px] font-extrabold mb-1.5 text-ink">{b.title}</div>
                    <ul className="list-disc pl-4 text-gray-700 text-[12.5px] leading-relaxed space-y-1 m-0">
                      {(b.points || []).map((pt, i) => (
                        <li key={i}>{pt}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center gap-3 py-3 px-5 border-t border-line bg-gray-50 shrink-0 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap text-muted text-[12.5px]">
              <span>Contact</span>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                className="text-brand-600 font-bold hover:text-brand-700 underline underline-offset-2 bg-transparent border-none cursor-pointer p-0"
                onClick={() =>
                  onCopyEmail(a.contactEmail || DEFAULT_CONTACT_EMAIL)
                }
                title="Copy email"
              >
                {a.contactEmail || DEFAULT_CONTACT_EMAIL}
              </button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <a
                className="border border-line bg-gray-50 text-ink hover:-translate-y-[1px] hover:shadow-sm rounded-lg px-3 py-2 font-bold text-sm transition-all inline-flex items-center gap-2 no-underline"
                href={`mailto:${a.contactEmail || DEFAULT_CONTACT_EMAIL}?subject=${encodeURIComponent(
                  `Request: ${a.title} Accelerator`,
                )}`}
              >
                <Mail size={13} strokeWidth={1.3} />
                Email now
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Shared Gallery Layout ---------------- */

export function AcceleratorGallery({
  eyebrow,
  title,
  subtitle,
  data,
  searchPlaceholder,
  emptyNoun
}) {
  const [q, setQ] = React.useState("");
  const [activeId, setActiveId] = React.useState(null);
  const [toast, setToast] = React.useState("");

  const list = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return data;
    return data.filter((a) => {
      const hay = [
        a.title,
        a.category,
        a.tagline,
        ...(a.overview || []),
        ...(a.benefits || []).map((b) => b.title),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [q, data]);

  const active = React.useMemo(
    () => data.find((a) => a.id === activeId) || null,
    [activeId, data]
  );

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(window.__accToastTimer);
    window.__accToastTimer = window.setTimeout(() => setToast(""), 1800);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Email copied");
    } catch {
      showToast("Couldn’t copy — try again");
    }
  };

  return (
    <div className="font-sans text-ink bg-transparent">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 mb-2">
        <div>
          <div className="text-[11px] font-bold tracking-widest uppercase text-brand-600 mb-1">{eyebrow}</div>
          <div className="text-[26px] font-extrabold tracking-tight leading-[1.1]">{title}</div>
          <div className="mt-1.5 text-muted text-[13.5px]">{subtitle}</div>
        </div>

        <div className="flex gap-2.5 items-center w-full lg:w-auto">
          <div className="flex items-center gap-2 bg-white border border-line rounded-xl px-3 py-2.5 w-full lg:min-w-[300px] shadow-sm transition-all focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/20">
            <span className="text-muted flex shrink-0" aria-hidden="true">
              <Search size={15} strokeWidth={1.5} />
            </span>
            <input
              className="border-none outline-none w-full text-[13.5px] text-ink bg-transparent placeholder-gray-400"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
            {q ? (
              <button
                className="bg-gray-100 text-muted w-5 h-5 rounded-full text-sm flex items-center justify-center shrink-0 hover:bg-gray-200 hover:text-ink cursor-pointer border-none"
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="text-muted text-[12.5px] font-semibold my-[14px]">
        <span>
          {list.length} {list.length === 1 ? emptyNoun : emptyNoun + "s"}
          {q ? <> matching “{q}”</> : null}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" role="list">
        {list.map((a) => {
          const config = getCategoryConfig(a.category);
          const initials =
            (a.title || "?")
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join("") || "A";
          const firstBenefit = a.benefits?.[0];

          return (
            <div
              className="relative flex h-[190px] bg-white border border-line rounded-[16px] overflow-hidden shadow-sm hover:-translate-y-[1px] hover:shadow-md transition-all duration-150 group"
              role="listitem"
              key={a.id}
            >
              <div className={`w-[4px] shrink-0 ${config.railClass}`} aria-hidden="true" />

              <div className="flex-1 flex flex-col min-w-0">
                <div className="flex gap-[14px] pt-[18px] px-[18px]">
                  <div className="w-14 h-14 rounded-xl border border-line bg-gradient-to-b from-white to-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
                    {a.logoUrl ? (
                      <img
                        className="w-full h-full object-contain p-2"
                        src={a.logoUrl}
                        alt={`${a.title} logo`}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb =
                            e.currentTarget.parentElement?.querySelector(".logo-fallback");
                          if (fb) fb.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="logo-fallback w-full h-full flex items-center justify-center font-extrabold text-ink tracking-wide text-[15px] bg-gray-50"
                      style={{ display: a.logoUrl ? "none" : "flex" }}
                    >
                      {initials}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="text-base font-extrabold truncate text-ink">{a.title}</div>
                      <StatusBadge map={ACCELERATOR_CATEGORY_MAP} status={a.category} size="sm" />
                    </div>
                    <div className="mt-1 text-muted text-[13px] leading-[1.4] line-clamp-2">{a.tagline}</div>
                  </div>
                </div>

                {firstBenefit ? (
                  <div className="flex items-center gap-2 mt-3 mx-[18px] px-3 py-2 bg-gray-50 border border-line rounded-lg text-[12.5px] text-gray-700">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${config.railClass}`} aria-hidden="true" />
                    <span className="truncate">
                      {firstBenefit.title}
                    </span>
                  </div>
                ) : null}

                <div className="flex gap-2.5 flex-wrap p-[14px] px-[18px] mt-auto">
                  <button
                    className="border border-transparent bg-transparent text-ink hover:bg-gray-50 rounded-lg px-3 py-2 font-bold text-[12.5px] transition-all inline-flex items-center gap-2 cursor-pointer focus-visible:outline-2 focus-visible:outline-brand-600 focus-visible:outline-offset-2"
                    type="button"
                    onClick={() => setActiveId(a.id)}
                    aria-haspopup="dialog"
                  >
                    More details
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!list.length ? (
        <div className="mt-[18px] p-[18px] px-5 border border-dashed border-line rounded-[14px] bg-white flex items-center justify-between gap-[14px] flex-wrap">
          <div>
            <div className="font-extrabold text-[14px] mb-[3px] text-ink">No {emptyNoun}s found</div>
            <div className="text-muted text-[13px]">
              Nothing matches “{q}”. Try a different keyword or category.
            </div>
          </div>
          <button className="border border-transparent bg-transparent text-ink hover:bg-gray-50 rounded-lg px-3 py-2 font-bold text-[12.5px] transition-all cursor-pointer" onClick={() => setQ("")} type="button">
            Clear search
          </button>
        </div>
      ) : null}

      {active ? (
        <DetailsModal
          accelerator={active}
          onClose={() => setActiveId(null)}
          onCopyEmail={copyToClipboard}
        />
      ) : null}

      {toast ? <div className="fixed right-[18px] bottom-[18px] bg-gray-900 text-white px-[14px] py-[10px] rounded-[10px] text-[13px] font-semibold shadow-2xl z-[80] animate-[acc-toast-in_0.18s_ease]">{toast}</div> : null}
    </div>
  );
}
